import type { ReactNode } from 'react';
import type { PinContentFn } from '../components/Sidebar/PinContent';
import type { UserLocalStorage } from '../types/localStorage';

import { Transition } from '@headlessui/react';
import { useCallback, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { putUserConfig } from '../lib/flotiq-client';
import { parseResponseErrors } from '../lib/flotiq-client/response-errors';
import FlotiqPlugins from '../lib/flotiq-plugins/flotiqPluginsRegistry';
import { LanguageChangedEvent } from '../lib/flotiq-plugins/plugin-events/LanguageChangedEvent';
import { getTestProps } from '../lib/helpers';

import GlobalSearch from '../components/GlobalSearch/GlobalSearch';

import { useAllSpaceConstraints } from '../hooks/api/useAllSpaceConstraints';
import useMediaUpload from '../hooks/api/useMediaUpload';
import { spaceSynchronizationModalId } from '../hooks/synchronization/useSpaceSynchronization';
import { useSpaceMenuItems } from '../hooks/UIPersonalization/useSpaceMenuItems';
import useOnce from '../hooks/useOnce';
import useSelectedSpace from '../hooks/useSelectedSpace';
import useToken from '../hooks/useToken';
import { useDowngradeModals } from './hooks/useDowngradeModals';
import { useLoadPlugins } from './hooks/useLoadPlugins';
import { useOnboardingModals } from './hooks/useOnboardingModals';

import { useModals } from '../contexts/ModalContext';

import { UnionIcon } from '../images/shapes';

import { replaceMediaModalId } from '../pages/EditMedia/EditMediaContainer';
import LoaderPage from '../pages/LoaderPage/LoaderPage';
import Structure from './Structure';
import { supportIssuesModalId } from './SupportIssueModal/useSupportIssues';

const testId = 'layout';

interface LayoutProps {
  /*
   ** Checking if the sidebar is open
   */
  isSidebarOpen?: boolean;
  /*
   ** Additional classes for layout
   */
  additionalClasses?: string;
  /**
   * Layout content
   */
  children?: ReactNode;
}

/**
 * @emits FlotiqPlugins."flotiq.language::changed"
 */
const Layout = ({ additionalClasses = '', children }: LayoutProps) => {
  const { t, i18n } = useTranslation();
  const { space, buildUrlWithSpace } = useSelectedSpace();
  const [user, setUser] = useLocalStorageState<UserLocalStorage>('cms.user');
  const jwt = useToken();
  const modal = useModals();

  const { handleOnboardingModals } = useOnboardingModals(testId);
  const { pluginsLoading, loadPlugins } = useLoadPlugins();
  const checkDowngradeNotices = useDowngradeModals(testId);

  useEffect(() => {
    checkDowngradeNotices();
  }, [checkDowngradeNotices]);

  useEffect(() => {
    loadPlugins();
  }, [loadPlugins]);

  useEffect(() => {
    handleOnboardingModals();
  }, [handleOnboardingModals]);

  const { allConstraints } = useAllSpaceConstraints(space);

  const updateUserMimeTypes = useCallback(async () => {
    const allowedMimetypes = allConstraints?.mimetypes;
    if (allowedMimetypes && user) {
      setUser({
        ...user,
        data: { ...user.data, allowedMimetypes },
      });
    }
  }, [allConstraints?.mimetypes, setUser, user]);

  useOnce(updateUserMimeTypes);

  const { onUpload } = useMediaUpload();

  const pinContentCallback = useCallback<PinContentFn>(
    async (id, pined) => {
      if (!user?.data) return;

      const removeElement = (arr: Record<string, string[]>, id: string) => {
        if (!space) return arr;
        const index = arr[space].indexOf(id);
        if (index > -1) {
          arr[space].splice(index, 1);
        }
        return arr;
      };

      let newPinnedContent = { ...user.data.config?.pinedDefinitions };

      if (!pined) {
        if (!space) return;
        if (!newPinnedContent[space]) {
          newPinnedContent[space] = [id];
        } else {
          newPinnedContent[space].push(id);
        }
      } else {
        newPinnedContent = removeElement(newPinnedContent, id);
      }

      const newConfig = {
        ...user.data.config,
        pinedDefinitions: newPinnedContent,
      };

      try {
        const { body, status, ok } = await putUserConfig(jwt, undefined, {
          id: user.data.id,
          config: newConfig,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        const newUser = { ...user };
        newUser.data.config = newConfig;

        setUser(newUser);
      } catch {
        toast.error(t('Form.CommunicationErrorMessage'));
      }
    },
    [jwt, setUser, space, t, user],
  );

  const { menuItems: sidebarMenuItems } = useSpaceMenuItems();

  useEffect(() => {
    const lngChangedCallback = (language: 'pl' | 'en') => {
      FlotiqPlugins.run(
        'flotiq.language::changed',
        new LanguageChangedEvent({ language }),
      );
      moment.locale(language);
    };

    i18n.on('languageChanged', lngChangedCallback);
    return () => i18n.off('languageChanged', lngChangedCallback);
  }, [i18n]);

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: File[] }) {
        onUpload(item.files);
      },
      canDrop: () =>
        !modal.isOpen(supportIssuesModalId) &&
        !modal.isOpen(spaceSynchronizationModalId) &&
        !modal.isOpen(replaceMediaModalId),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onUpload, modal],
  );

  return pluginsLoading ? (
    <LoaderPage />
  ) : (
    <>
      <GlobalSearch />
      <Structure
        testId={testId}
        showUpgrade
        additionalClasses={additionalClasses}
        ref={() => {
          drop(document.body);
        }}
        logoLink={space ? buildUrlWithSpace('') : '/'}
        sidebarMenuItems={sidebarMenuItems}
        pinContentCallback={pinContentCallback}
        additionalElement={
          <Transition show={canDrop && isOver}>
            <div className="z-50 ease-in duration-200 data-closed:opacity-0">
              <div className="fixed top-0 right-0 h-screen w-full bg-white opacity-75 z-50" />
              <div
                className={twMerge(
                  'fixed top-1/2 left-1/2 bg-blue aspect-square rounded-full z-50',
                  'h-60 md:h-80 lg:h-96 2xl:h-[500px]',
                  'text-white font-bold text-center',
                  'text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl -translate-x-1/2 -translate-y-1/2',
                )}
                {...getTestProps(testId, 'add-media')}
              >
                <div className="absolute h-full w-full animate-slowPing rounded-full bg-blue opacity-20" />
                <div
                  className={
                    'h-full w-full p-5 md:p-8 lg:py-10 2xl:py-20 grid grid-rows-3 justify-center items-center'
                  }
                >
                  <UnionIcon className="animate-slowSpin justify-self-center h-2 md:h-4 lg:h-6 opacity-80" />
                  <p>{t('Media.DropFiles')}</p>
                  <p className="px-5 2xl:px-20 self-end opacity-80 text-base md:text-2xl 2xl:text-3xl">
                    {t('Media.AddToLibrary')}
                  </p>
                </div>
              </div>
            </div>
          </Transition>
        }
      >
        {children ? children : <Outlet />}
      </Structure>
    </>
  );
};

export default Layout;
